import React, { createContext, useContext, useState, useEffect } from "react";
import { AppMode, SessionState } from "../types";
import { AppPage, Notice } from "./MonyawnContext.types";
import { MobileTab } from "../components/layout/MobileNavigation/MobileNavigation.contract";

interface SessionContextType extends SessionState {
  currentPage: AppPage;
  setCurrentPage: (page: AppPage) => void;
  mobileTab: MobileTab;
  setMobileTab: (tab: MobileTab) => void;
  notice: Notice;
  setNotice: (notice: Notice) => void;

  setMode: (mode: AppMode) => void;
  setSelectedOpportunity: (id: string) => void;
  setSelectedAccount: (id: string) => void;
  setSelectedUser: (id: string) => void;
  setLastSaved: (at: string) => void;
  setLastExported: (at: string) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedOpportunityId, setSelectedOpportunityId] = useState("");
  const [currentMode, setCurrentMode] = useState<AppMode>("user");
  const [lastSavedAt, setLastSavedAt] = useState("");
  const [lastExportedAt, setLastExportedAt] = useState("");

  const [currentPage, setCurrentPage] = useState<AppPage>("login");
  const [mobileTab, setMobileTab] = useState<MobileTab>("home");
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace(/^#/, "");
      const routeMap: Record<string, AppPage> = {
        login: "login",
        start: "start",
        "proof-drop": "proof-drop",
        confirm: "confirm",
        "setup-base": "setup-base",
        workspace: "workspace",
        about: "about",
        support: "support",
        privacy: "privacy",
        terms: "terms",
        cookies: "cookies",
        security: "security",
        "acceptable-use": "acceptable-use",
        signal: "proof-drop",
        intake: "setup-base",
      };
      setCurrentPage(routeMap[hash] ?? "login");
    };
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange(); // Initial route
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const value = {
    selectedAccountId,
    selectedUserId,
    selectedOpportunityId,
    currentMode,
    lastSavedAt,
    lastExportedAt,
    currentPage,
    setCurrentPage,
    mobileTab,
    setMobileTab,
    notice,
    setNotice,
    setMode: setCurrentMode,
    setSelectedOpportunity: setSelectedOpportunityId,
    setSelectedAccount: setSelectedAccountId,
    setSelectedUser: setSelectedUserId,
    setLastSaved: setLastSavedAt,
    setLastExported: setLastExportedAt,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used within SessionProvider");
  return context;
};
